"use client";

import * as React from "react";
import { Plus, X } from "lucide-react";
import { Badge } from "@src/components/ui/badge";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@src/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@src/components/ui/popover";
import { cn } from "@lib/utils/ui";
export type Option = {
  label: string;
  value: string;
};
interface MultiSelectProps {
  options: Option[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  className?: string;
  emptyText?: string;
}
export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = "Select options",
  className,
  emptyText = "No options found."
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);
  const handleUnselect = (value: string) => {
    onChange(selected.filter(item => item !== value));
  };
  const handleSelect = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter(item => item !== value));
    } else {
      onChange([...selected, value]);
    }
  };
  return <Popover open={open} onOpenChange={setOpen} data-sentry-element="Popover" data-sentry-component="MultiSelect" data-sentry-source-file="multi-select.tsx">
      <PopoverTrigger asChild data-sentry-element="PopoverTrigger" data-sentry-source-file="multi-select.tsx">
        <div role="combobox" aria-expanded={open} aria-controls="multi-select-options" className={cn("flex min-h-10 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50", className)} onClick={() => setOpen(!open)}>
          <div className="flex flex-wrap gap-1">
            {selected.length === 0 && <span className="text-muted-foreground">{placeholder}</span>}
            {selected.map(value => {
            const option = options.find(opt => opt.value === value);
            return <Badge key={value} variant="secondary" className="mr-1 mb-1">
                  {option?.label}
                  <button className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2" onKeyDown={e => {
                if (e.key === "Enter") {
                  handleUnselect(value);
                }
              }} onMouseDown={e => {
                e.preventDefault();
                e.stopPropagation();
              }} onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                handleUnselect(value);
              }}>
                    <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                  </button>
                </Badge>;
          })}
            {selected.length !== 0 && <Badge variant="secondary" className="mr-1 mb-1 cursor-pointer">
                <Plus className="h-3 w-3 text-muted-foreground hover:text-foreground" />
              </Badge>}
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start" data-sentry-element="PopoverContent" data-sentry-source-file="multi-select.tsx">
        <Command data-sentry-element="Command" data-sentry-source-file="multi-select.tsx">
          <CommandInput placeholder="Search options..." data-sentry-element="CommandInput" data-sentry-source-file="multi-select.tsx" />
          <CommandList data-sentry-element="CommandList" data-sentry-source-file="multi-select.tsx">
            <CommandEmpty data-sentry-element="CommandEmpty" data-sentry-source-file="multi-select.tsx">{emptyText}</CommandEmpty>
            <CommandGroup className="max-h-64 overflow-auto" data-sentry-element="CommandGroup" data-sentry-source-file="multi-select.tsx">
              {options.map(option => {
              const isSelected = selected.includes(option.value);
              return <CommandItem key={option.value} onSelect={() => handleSelect(option.value)} className={cn("flex items-center gap-2", isSelected ? "bg-accent" : "")}>
                    <div className={cn("flex h-4 w-4 items-center justify-center rounded-sm border border-primary", isSelected ? "bg-primary text-primary-foreground" : "opacity-50 [&_svg]:invisible")}>
                      <svg className={cn("h-3 w-3")} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span>{option.label}</span>
                  </CommandItem>;
            })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>;
}